import { useCallback, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import {
  ShieldCheck,
  ShieldAlert,
  AlertCircle,
  RefreshCw,
  Clock,
  Folder,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { getConciergeStatus, type ConciergeStatus } from '../lib/api'
import { cn } from '../lib/utils'

// The calm home status — "a story, not an alert" (PRODUCT_VISION_agent_team.md).
// This card asks the LIVE Concierge status endpoint
// (GET /api/v1/concierge/status/{principal_id}) how things actually stand and
// renders the team's OWN plain-language summary, not a status the FE derived
// from a raw alert list. If the team can't be reached we say so honestly —
// "never a fake calm" — and never fall back to a green all-clear.

// The backend's known status strings. Anything else is treated as
// "attention_needed" (fail toward asking the client to look, never toward a
// fabricated all-clear). could_not_look is its own honest branch — a 200 that
// says we failed to look is not "Attention needed" and never "All clear".
const ALL_CLEAR = 'all_clear'
const COULD_NOT_LOOK = 'could_not_look'

function relativeLastCheck(iso: string): string | null {
  // The backend sends an ISO8601 timestamp. If it's unparseable we simply omit
  // the "checked …" line rather than render "Invalid Date".
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  try {
    return formatDistanceToNow(new Date(t), { addSuffix: true })
  } catch {
    return null
  }
}

export function HomeStatusCard({ principalId }: { principalId: string }) {
  const [status, setStatus] = useState<ConciergeStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await getConciergeStatus(principalId)
      setStatus(data)
    } catch (err) {
      // Honest failure: we could NOT confirm the state. Clear any stale status
      // so the UI can never show a green all-clear it didn't just verify.
      setStatus(null)
      setError(err instanceof Error ? err.message : 'unknown error')
    } finally {
      setIsLoading(false)
    }
  }, [principalId])

  useEffect(() => {
    void load()
  }, [load])

  // Loading — a quiet skeleton, no premature verdict.
  if (isLoading && !status && !error) {
    return (
      <div
        className="rounded-2xl p-4 mb-5 border border-gray-200 bg-white animate-pulse"
        role="status"
        aria-label="Checking with your security team"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gray-100 flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-32 rounded bg-gray-100" />
            <div className="h-3 w-48 rounded bg-gray-100" />
          </div>
        </div>
      </div>
    )
  }

  // Honest failure — never a fabricated all-clear (PRODUCT_VISION "never a
  // fake calm"). We say plainly that we couldn't check and offer a retry.
  if (error || !status) {
    return (
      <div
        role="alert"
        className="rounded-2xl p-4 mb-5 border border-danger/30 bg-danger-light"
      >
        <div className="flex items-start gap-3">
          <AlertCircle
            className="w-5 h-5 text-danger flex-shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-navy">
              We couldn’t check just now
            </h2>
            <p className="text-xs text-gray-600 mt-0.5">
              We couldn’t reach your security team to confirm how things stand
              {error ? ` (${error})` : ''}. This is not an all-clear — please
              try again.
            </p>
            <button
              type="button"
              onClick={() => void load()}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-danger px-3 py-1.5 text-xs font-semibold text-white hover:bg-danger/90 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  const allClear = status.status === ALL_CLEAR
  const couldNotLook = status.status === COULD_NOT_LOOK
  const lastCheck = status.last_check ? relativeLastCheck(status.last_check) : null

  const headline = allClear
    ? 'All clear'
    : couldNotLook
      ? 'We could not look'
      : 'Attention needed'

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        'rounded-2xl p-4 mb-5 border',
        allClear
          ? 'bg-success-light border-success/20'
          : 'bg-warning-light border-warning/20',
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
            allClear ? 'bg-success/10' : 'bg-warning/10',
          )}
        >
          {allClear ? (
            <ShieldCheck className="w-5 h-5 text-success" aria-hidden="true" />
          ) : (
            <ShieldAlert className="w-5 h-5 text-warning" aria-hidden="true" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-navy">{headline}</h2>
          {/* The team's OWN words — rendered verbatim, never paraphrased. */}
          <p className="text-sm text-gray-600 mt-0.5 whitespace-pre-wrap break-words">
            {status.summary}
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500">
            {status.open_cases > 0 && (
              <span className="inline-flex items-center gap-1">
                <Folder className="w-3.5 h-3.5" aria-hidden="true" />
                {status.open_cases} open{' '}
                {status.open_cases === 1 ? 'case' : 'cases'}
              </span>
            )}
            {lastCheck && (
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                Checked {lastCheck}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={isLoading}
          className="tap-min p-2 rounded-lg text-gray-400 hover:text-brass hover:bg-brass/10 disabled:opacity-50 transition-colors flex-shrink-0"
          aria-label="Re-check status"
        >
          <RefreshCw
            className={cn('w-4 h-4', isLoading && 'animate-spin')}
            aria-hidden="true"
          />
        </button>
      </div>
    </motion.div>
  )
}
